import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCirclePlus } from '@fortawesome/free-solid-svg-icons';
import { useParams } from 'react-router-dom';
import { RcFile } from 'antd/es/upload';
import Button from '../../../../../core/components/button/Button';
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks';
import {
  enqAttachmentsFiltersSelector,
  enqAttachmentsSelector,
} from '../../../../../store/selectors/enquiriesSelector';
import {
  createEnquiryAttachmentThunk,
  deleteMultipleEnquiryAttachmentThunk,
  getEnquiryAttachmentsThunk,
} from '../../../../../store/thunks/enquiry/attachmentsThunks';
import EnquiryAttachmentsTable from './table/EnquiryAttachmentsTable';
import Pagination from '../../../../../core/components/pagination/Pagination';
import { setAttachmentsTableFilters } from '../../../../../store/slices/enquiriesSlice';
import ConfirmationModal from '../../../../../core/components/confirmation-modal/ConfirmationModal';
import CreateAttachmentModal from '../../../../../common/components/create-attachment/CreateAttachmentModal';
import { setLoading } from '../../../../../store/slices/coreSlice';
import { guidGenerator } from '../../../../../core/utils/guidGenerator';
import Restricted from '../../../../../permissions-handling/Restricted';
import { PermissionEnum } from '../../../../../core/enums/dictionariesEnums';
import { BIG_TABLES_PAGE_SIZES } from '../../../../../core/utils/regex';
import { scrollAttachTable } from '../../../../../common/utils/scrollAttachTable';
import { useSelectAll } from '../../../../../core/hooks/useSelectAll';

const EnquiryAttachments: React.FC = () => {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const attachmentsFilters = useAppSelector(enqAttachmentsFiltersSelector);
  const { items, allItemIds, totalCount } = useAppSelector(enqAttachmentsSelector);
  const [fileList, setFileList] = useState<RcFile[]>([]);
  const [comment, setComment] = useState('');
  const [isCreateModal, setCreateModal] = useState(false);
  const [isDeleteModal, setDeleteModal] = useState(false);

  const {
    isAll,
    isIndeterminate,
    checkedKeys,
    checkAllHandle,
    checkRowHandle,
  } = useSelectAll(allItemIds);

  const deleteSelected = () => {
    id && dispatch(deleteMultipleEnquiryAttachmentThunk({
      attachmentIds: checkedKeys,
      enquiryId: +id,
      closeModal: () => setDeleteModal(false),
    }));
  };
  const cancelHandle = () => {
    setCreateModal(false);
    setFileList([]);
    setComment('');
  };

  const uploadHandle = async () => {
    dispatch(setLoading(true));
    const list = fileList.map((el) => ({
      fileName: el.name,
      uploadGuid: guidGenerator(),
      file: el,
    }));
    if (list.length > 0) {
      id && dispatch(createEnquiryAttachmentThunk({
        enquiryId: +id,
        list,
        comment,
        onCancel: () => cancelHandle(),
      }));
    }
  };

  useEffect(() => {
    id && dispatch(getEnquiryAttachmentsThunk({
      enquiryId: +id,
      filters: { ...attachmentsFilters },
    }));
    // eslint-disable-next-line
    }, [id, attachmentsFilters]);

  return (
    <>
      <CreateAttachmentModal
        isVisible={isCreateModal}
        onCancel={cancelHandle}
        uploadHandle={uploadHandle}
        fileList={fileList}
        setFileList={setFileList}
        comment={comment}
        setComment={setComment}
      />
      <ConfirmationModal
        isVisible={isDeleteModal}
        onCancel={() => {
          setDeleteModal(false);
        }}
        confirmHandler={deleteSelected}
      />
      <div className="attachments">
        <div className="attachments__head actions-and-paging">
          <div className="attachmentsActions actions-and-paging__actions">
            <Restricted to={PermissionEnum.EnquiryEditFields}>
              <>
                <Button
                  label="Upload file"
                  onClick={() => setCreateModal(true)}
                  icon={<FontAwesomeIcon icon={faCirclePlus} />}
                  designType="dark"
                  reversed
                />
                <Button
                  label={`Delete selected ${checkedKeys.length ? `(${checkedKeys.length})` : ''}`}
                  onClick={() => setDeleteModal(true)}
                  disabled={!checkedKeys.length}
                />
              </>
            </Restricted>
          </div>
          {totalCount > 0 && <Pagination
            currentPage={attachmentsFilters.page}
            pageSize={attachmentsFilters.pageSize}
            onChange={(page, pageSize) => {
              scrollAttachTable(attachmentsFilters.page, page);
              dispatch(setAttachmentsTableFilters({ ...attachmentsFilters, page, pageSize }));
            }}
            totalItems={totalCount}
            pageSizeOptions={BIG_TABLES_PAGE_SIZES}
            narrow
            className="actions-and-paging__paging"
          />}
        </div>
        <EnquiryAttachmentsTable
          attachments={items}
          checkedKeys={checkedKeys}
          isAll={isAll}
          indeterminate={isIndeterminate}
          checkAllHandle={checkAllHandle}
          checkRow={checkRowHandle}
        />
      </div>
    </>
  );
};

export default EnquiryAttachments;
